import { useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import { DrawingApprovalBadge, DrawingApprovalTimeline } from "@/components/DrawingApprovalBadge";
import { HoldBadge, HOLDS } from "@/components/HoldBadge";
import { ApprovalTrail } from "@/components/ApprovalTrail";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  PencilRuler,
  Receipt,
  Plus,
  Trash,
  ArrowsIn,
  Eraser,
  ArrowsOutSimple,
  FileArrowUp,
  Cube,
  FloppyDisk,
  Printer,
  Eye,
  EyeSlash,
  PaperPlaneTilt,
  FilePdf,
  ArrowSquareOut,
  ArrowsClockwise,
  Briefcase,
  CalendarPlus,
  Stack,
  ImageSquare,
  Warning,
  CheckCircle,
  DotsThreeVertical,
  Package,
  HandCoins,
  SealCheck,
  LinkBreak,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoveAccountDialog } from "@/components/MoveAccountDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { AddressCombo } from "@/components/AddressCombo";
import { DateField, TimeField } from "@/components/DateTimeField";
import { ARRIVAL_WINDOWS } from "@/lib/scheduleWindows";
import { AssistantDock } from "@/components/AssistantDock";
import { RoomMockups } from "@/components/RoomMockups";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CUSTOMER_GROUPS } from "@/pages/Customers";
import { SearchSelect } from "@/components/SearchSelect";
import {
  CountertopDesigner,
  DesignerPlan,
  splitPiece,
} from "@/components/CountertopDesigner";
import { mergeSeamIn, uploadTracePhoto } from "@/lib/quoteAreas";
import { useUndoHistory } from "@/lib/useUndoHistory";
import { SlabLayoutBoard } from "@/components/SlabLayoutBoard";
import { PortalThread } from "@/components/PortalThread";
import { QuotePrintout } from "@/components/QuotePrintout";
import { QuoteLines, resolveLine, lineMarginPct } from "@/components/QuoteLines";
import { ColourOptionsDialog } from "@/components/ColourOptionsDialog";
import { ColourChoiceBanner } from "@/components/ColourChoiceBanner";
import { QuoteEmailDialog } from "@/components/QuoteEmailDialog";
import { QuoteStockDialog } from "@/components/QuoteStockDialog";
import { api, API, downloadFile, previewFile, formatApiErrorDetail, photoHref } from "@/lib/api";
import { formatInches } from "@/lib/measure";

const printSignedAgreement = (sig) => {
  const w = window.open("", "_blank", "width=820,height=940");
  if (!w) return;
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const paras = (sig.body || "")
    .split("\n")
    .map((l) => (l.trim() ? `<p>${esc(l)}</p>` : "<br/>"))
    .join("");
  w.document.write(
    `<!doctype html><html><head><title>${esc(sig.title || "Agreement")}</title>` +
      `<style>body{font-family:Georgia,'Times New Roman',serif;color:#1e293b;max-width:720px;margin:40px auto;padding:0 28px;line-height:1.55}h1{font-size:22px;margin-bottom:20px}p{margin:0 0 12px}hr{border:none;border-top:1px solid #cbd5e1;margin:28px 0}.sig img{max-height:90px;display:block;margin:6px 0}.muted{color:#64748b;font-size:13px}</style></head><body>` +
      `<h1>${esc(sig.title || "Agreement")}</h1>${paras}<hr/>` +
      `<div class="sig"><p><strong>Signed by:</strong> ${esc(sig.name)}</p>` +
      (sig.image ? `<img src="${sig.image}" alt="signature"/>` : "") +
      `<p class="muted">Date: ${esc(new Date(sig.at).toLocaleString())}${sig.ip ? ` · IP ${esc(sig.ip)}` : ""}</p></div></body></html>`,
  );
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 350);
};

const STEPS = [
  {
    label: "Project",
    hint: "Customer, site and material",
    icon: Briefcase,
    step: 0,
  },
  {
    label: "Design Studio",
    hint: "Full-screen drawing, slab layout, vein match & 3D",
    icon: Cube,
    action: "design",
  },
  {
    label: "Quote",
    hint: "Lines, services and pricing",
    icon: Receipt,
    step: 3,
  },
  {
    label: "Review",
    hint: "Print, email or accept to start the project",
    icon: CheckCircle,
    step: 4,
  },
];

const YIELD_STYLE = {
  ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  warn: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  block: "border-red-500/40 bg-red-500/10 text-red-200",
};

const YieldBanner = ({ info }) => {
  if (!info) return null;
  return (
    <div
      data-testid="slab-yield-banner"
      className={`rounded-xl border p-3.5 ${YIELD_STYLE[info.level] || YIELD_STYLE.warn}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {info.level === "ok" ? (
          <CheckCircle size={16} weight="bold" />
        ) : (
          <Warning size={16} weight="bold" />
        )}
        <span
          className="text-xs font-bold uppercase tracking-widest"
          data-testid="slab-yield-level"
        >
          {info.level === "ok"
            ? "Slab yield ok"
            : info.level === "block"
              ? "Piece will not fit the slab"
              : "Check slab yield"}
        </span>
        <span
          className="font-mono-data text-[11px] opacity-90"
          data-testid="slab-yield-stats"
        >
          {info.pieces_sqft} ft² drawn · {info.needed_sqft} ft² with{" "}
          {info.waste_pct}% waste · {info.slabs_needed} slab(s) needed
          {info.slabs_available > 0
            ? ` of ${info.slabs_available} in stock`
            : ""}{" "}
          · slab {formatInches(info.slab_length_in)} x{" "}
          {formatInches(info.slab_width_in)} ({info.source})
        </span>
      </div>
      {info.messages.length > 0 && (
        <ul
          className="mt-2 space-y-1 text-xs"
          data-testid="slab-yield-messages"
        >
          {info.messages.map((m) => (
            <li key={m}>· {m}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
const uid = () => Math.random().toString(36).slice(2, 10);
const money = (v) =>
  `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const emptyQuote = {
  project_name: "",
  customer_id: null,
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  customer_address: "",
  contact_name: "",
  contact_email: "",
  job_address: "",
  trade_account: false,
  pay_plan: "",
  slab_color: "",
  stone_group: "Group 0",
  thickness: "3cm",
  status: "Draft",
  discount: 0,
  discount_pct: 0,
  notes: "",
  areas: [{ id: uid(), name: "Kitchen", address: "", pieces: [], cutouts: [] }],
  lines: [],
  layout: {},
};

const KIND_LABEL = {
  slab_piece: "Slab piece",
  slab: "Slab",
  remnant: "Remnant",
  sink: "Sink",
};

const EMPTY_ACCOUNT = {
  name: "",
  company: "",
  phone: "",
  email: "",
  address: "",
  group: "Regular",
};

const EMPTY_SLAB = {
  slab_color: "",
  material: "Granite",
  finish: "Polished",
  thickness: "3cm",
  stone_group: "Group 0",
  supplier: "",
  length_in: 126,
  width_in: 63,
  quantity: 1,
};

export default function QuoteBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const dxfRef = useRef(null);
  const [step, setStep] = useState(() => {
    const s = Number(new URLSearchParams(window.location.search).get("step"));
    return [0, 3, 4].includes(s) ? s : 0;
  });
  const [q, setQ] = useState(emptyQuote);
  const layoutHistory = useUndoHistory(q, setQ);
  const [totals, setTotals] = useState(null);
  const [template, setTemplate] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [meta, setMeta] = useState({
    groups: [],
    edge_profiles: [],
    edge_styles: [],
    cutout_kinds: [],
    trade_discounts: {},
  });
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const accountReqRef = useRef(null);
  const [colors, setColors] = useState([]);
  const [sinks, setSinks] = useState([]);
  const [cutTemplates, setCutTemplates] = useState([]);
  const [customLayouts, setCustomLayouts] = useState([]);
  const putCustomLayouts = async (items, msg) => {
    setCustomLayouts(items);
    try {
      await api.put("/settings/custom-layouts", { items });
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error("Could not save the layout tile");
    }
  };
  const [savedLayouts, setSavedLayouts] = useState([]);
  const [rateBands, setRateBands] = useState({});
  const [services, setServices] = useState([]);
  const [slabStock, setSlabStock] = useState([]);
  const [remnants, setRemnants] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [newAcc, setNewAcc] = useState(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [newContact, setNewContact] = useState(null);
  const [newSlab, setNewSlab] = useState(null);
  const [fsArea, setFsArea] = useState(null);
  const [stages, setStages] = useState([]);
  const [areaTab, setAreaTab] = useState(null);

  useEffect(() => {
    if (!fsArea) return;
    document.body.classList.add("designer-fullscreen");
    const onKey = (e) => e.key === "Escape" && setFsArea(null);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("designer-fullscreen");
      window.removeEventListener("keydown", onKey);
    };
  }, [fsArea]);
  const [yieldInfo, setYieldInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [stockAreas, setStockAreas] = useState([]);
  const [stockOpen, setStockOpen] = useState(false);
  const [commission, setCommission] = useState(null);
  useEffect(() => {
    if (!id || id === "new") return;
    api.post(`/quotes/${id}/commission/preview`).then(({ data }) => setCommission(data)).catch(() => {});
  }, [id, q?.customer_id, q?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!id || id === "new" || !(q?.stock_holds || []).length) return;
    api.get(`/quotes/${id}/stock`).then(({ data }) => setStockAreas(data.areas || [])).catch(() => {});
  }, [id, (q?.stock_holds || []).length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [schedType, setSchedType] = useState("Install");
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [schedWindow, setSchedWindow] = useState("All day");
  const [schedCrew, setSchedCrew] = useState("");
  const [schedBusy, setSchedBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewId, setPreviewId] = useState("");
  const skipReloadRef = useRef(null);
  const saveInFlightRef = useRef(null);
  const lastSavedRef = useRef(null);
  const isNew = !id || id === "new";
  const approval = q.drawing_approval || {};

  const load = useCallback(async () => {
    const [p, acc, cat, sk, ct, sv, sl, rm, dl, rb, cb] = await Promise.all([
      api.get("/settings/pricing"),
      api.get("/customers", { params: { fields: "basic" } }),
      api.get("/inventory/catalog"),
      api.get("/sinks"),
      api.get("/settings/cutout-templates"),
      api.get("/services"),
      api.get("/slabs"),
      api.get("/remnants"),
      api.get("/settings/drawing-layouts").catch(() => ({ data: [] })),
      api.get("/settings/rate-bands").catch(() => ({ data: {} })),
      api.get("/cabinets").catch(() => ({ data: [] })),
    ]);
    setMeta({
      groups: p.data.groups,
      rates: p.data.pricing?.groups || {},
      edge_profiles: p.data.edge_profiles,
      edge_styles: p.data.edge_styles || [],
      cutout_kinds: p.data.cutout_kinds,
      drawing_general: p.data.drawing_general || {},
      drawing_kits: p.data.drawing_kits || [],
      trade_discounts: p.data.pricing?.trade_discounts || {},
      min_margin_pct: Number(p.data.pricing?.min_margin_pct || 0),
      show_profit_strip: p.data.pricing?.show_profit_strip !== false,
    });
    setAccounts(acc.data);
    setColors(cat.data.slabs);
    setCabinets(cb.data || []);
    setSinks(
      (sk.data || []).map((s) => ({
        id: s.id,
        label: [s.brand, s.model].filter(Boolean).join(" "),
        qty: s.quantity,
        sink_type: s.sink_type,
        size: s.size || "",
        cutout_size: s.cutout_size || "",
        description: s.description || "",
        price: Number(s.sale_price ?? s.cost ?? 0),
      })),
    );
    setCutTemplates(ct.data || []);
    setSavedLayouts(dl.data || []);
    api.get("/settings/custom-layouts").then((r) => setCustomLayouts(r.data || [])).catch(() => {});
    setRateBands(rb.data || {});
    setServices(sv.data || []);
    setSlabStock((sl.data || []).filter((s) => s.status === "In Stock"));
    setRemnants((rm.data || []).filter((r) => r.status === "Available"));
    if (!isNew) {
      if (skipReloadRef.current === id) return; // just created/saved — keep memory copy
      const { data } = await api.get(`/quotes/${id}`);
      setQ(data);
      setTotals(data.totals);
      skipReloadRef.current = id;
      lastSavedRef.current = JSON.stringify(data);
    } else {
      skipReloadRef.current = null;
      lastSavedRef.current = null;
      // opened from an account or a project — carry the details across
      const cid = params.get("customer");
      const account = cid
        ? await api.get(`/customers/${cid}`).then((r) => r.data).catch(() => null)
        : null;
      if (account) {
        accountReqRef.current = cid;
        setAccount(account);
      }
      if (cid || params.get("project"))
        setQ((cur) => ({
          ...cur,
          project_name: params.get("name") || cur.project_name,
          job_address: params.get("address") || cur.job_address,
          customer_id: cid || cur.customer_id,
          customer_name: account?.name || cur.customer_name,
          customer_email: account?.email || cur.customer_email,
          customer_phone: account?.phone || cur.customer_phone,
          customer_address: account?.address || cur.customer_address,
        }));
    }
  }, [id, isNew, params]);

  useEffect(() => {
    load();
    api
      .get("/settings/quote-template")
      .then(({ data }) => setTemplate(data))
      .catch(() => setTemplate(null));
    api
      .get("/settings/branding")
      .then(({ data }) => setHasLogo(!!data.logo))
      .catch(() => setHasLogo(false));
    api
      .get("/settings/quote-stages")
      .then(({ data }) => setStages(data.stages || []))
      .catch(() => setStages([]));
  }, [load]);

  const pieceCount = q.areas.reduce((n, a) => n + (a.pieces || []).length, 0);

  // Only the quote's own account is loaded in full (contacts, group, addresses);
  // the picker list is names-only (/customers?fields=basic).
  const loadAccount = useCallback(async (cid, fillContact = false) => {
    accountReqRef.current = cid;
    if (!cid) {
      setAccount(null);
      return;
    }
    try {
      const { data } = await api.get(`/customers/${cid}`);
      if (accountReqRef.current !== cid) return;
      setAccount(data);
      if (fillContact)
        setQ((cur) => ({
          ...cur,
          customer_name: data.name || cur.customer_name,
          customer_email: data.email || "",
          customer_phone: data.phone || "",
          customer_address: data.address || "",
        }));
    } catch {
      if (accountReqRef.current === cid) setAccount(null);
    }
  }, []);

  useEffect(() => {
    const cid = q.customer_id || null;
    if (accountReqRef.current === cid) return;
    loadAccount(cid);
  }, [q.customer_id, loadAccount]);
  const accountEmails = [
    ...new Set(
      [
        account?.email,
        ...(account?.contacts || []).map((ct) => ct.email),
      ].filter(Boolean),
    ),
  ].map((e) => {
    const ct = (account?.contacts || []).find((c) => c.email === e);
    return { value: e, label: e, hint: ct ? ct.name : "Account" };
  });
  const accGroup = account?.group || q.customer_group || "";
  const tradePct = Number((meta.trade_discounts || {})[accGroup] || 0);

  const saveNewSlab = async () => {
    if (!newSlab?.slab_color.trim()) {
      toast.error("Give the slab a color name");
      return;
    }
    try {
      const { data } = await api.post("/slabs", {
        ...newSlab,
        length_in: Number(newSlab.length_in) || 0,
        width_in: Number(newSlab.width_in) || 0,
        quantity: Number(newSlab.quantity) || 1,
      });
      const cat = await api.get("/inventory/catalog");
      setColors(cat.data.slabs);
      setQ((cur) => ({ ...cur, slab_color: data.slab_color }));
      setNewSlab(null);
      toast.success(`${data.slab_color} added to inventory`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add the slab",
      );
    }
  };

  const putCutTemplates = async (items, msg) => {
    setCutTemplates(items);
    try {
      await api.put("/settings/cutout-templates", { items });
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error("Could not save the cut-out setup");
    }
  };

  const putLayouts = async (items, msg) => {
    setSavedLayouts(items);
    try {
      await api.put("/settings/drawing-layouts", { items });
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error("Could not save the layout");
    }
  };

  const saveNewContact = async () => {
    if (!newContact?.name.trim() || !account) {
      toast.error("Give the contact a name");
      return;
    }
    try {
      const { data } = await api.put(`/customers/${account.id}`, {
        name: account.name,
        company: account.company || "",
        phone: account.phone || "",
        email: account.email || "",
        address: account.address || "",
        status: account.status || "Lead",
        group: account.group || "Regular",
        notes: account.notes || "",
        contacts: [...(account.contacts || []), newContact],
        job_addresses: account.job_addresses || [],
      });
      setAccounts((prev) => prev.map((a) => (a.id === data.id ? data : a)));
      setAccount(data);
      setQ((cur) => ({ ...cur, contact_name: newContact.name }));
      setNewContact(null);
      toast.success(`${newContact.name} added to ${data.name}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add the contact",
      );
    }
  };

  const saveNewAccount = async () => {
    if (!newAcc?.name.trim()) {
      toast.error("Give the account a name");
      return;
    }
    try {
      const { data } = await api.post("/customers", {
        ...newAcc,
        status: "Lead",
      });
      setAccounts((prev) => [data, ...prev]);
      accountReqRef.current = data.id;
      setAccount(data);
      setQ((cur) => ({
        ...cur,
        customer_id: data.id,
        customer_name: data.name,
        customer_email: data.email || "",
        customer_phone: data.phone || "",
        customer_address: data.address || "",
      }));
      setNewAcc(null);
      toast.success(`${data.name} added`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add the account",
      );
    }
  };

  useEffect(() => {
    if (!pieceCount) {
      setYieldInfo(null);
      return;
    }
    let stale = false;
    const t = setTimeout(() => {
      api
        .post("/quotes/yield-check", q)
        .then(({ data }) => {
          if (!stale) setYieldInfo(data);
        })
        .catch(() => setYieldInfo(null));
    }, 400);
    return () => {
      stale = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieceCount, q.slab_color, q.thickness, JSON.stringify(q.areas)]);

  const save = async (patch = {}, opts = {}) => {
    const named = (patch.project_name ?? q.project_name ?? "").trim();
    // never block a save on a missing name — fall back to something sensible
    const fallback =
      named ||
      [q.customer_name, q.job_address, q.areas?.[0]?.name]
        .map((s) => (s || "").trim())
        .find(Boolean) ||
      `New quote ${new Date().toLocaleDateString()}`;
    const body = { ...q, ...patch, project_name: fallback };
    if (saveInFlightRef.current) return saveInFlightRef.current;
    const run = async () => {
    setBusy(true);
    setSaveState("saving");
    try {
      const { data } = isNew
        ? await api.post("/quotes", body)
        : await api.put(`/quotes/${id}`, body);
      setQ(data);
      setTotals(data.totals);
      skipReloadRef.current = data.id;
      lastSavedRef.current = JSON.stringify(data);
      setSaveState("saved");
      if (isNew) {
        const projectId = params.get("project");
        if (projectId)
          await api
            .post(`/projects/${projectId}/attach`, { quote_ids: [data.id] })
            .catch(() => {});
        navigate(`/quotes/${data.id}`, { replace: true });
      }
      if (!named)
        toast.info(`Named it "${fallback}" — rename it on the Project step`);
      if (!opts.silent)
        toast.success(
          opts.draft
            ? "Saved as draft"
            : opts.stage
              ? `Stage set to ${data.status}`
              : "Quote saved",
        );
      return data;
    } catch (err) {
      setSaveState("error");
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save quote",
      );
    } finally {
      setBusy(false);
    }
    };
    saveInFlightRef.current = run().finally(() => {
      saveInFlightRef.current = null;
    });
    return saveInFlightRef.current;
  };

  // Manual save only — track unsaved edits and warn before leaving with changes.
  useEffect(() => {
    setDirty(JSON.stringify(q) !== lastSavedRef.current);
  }, [q]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const setStage = (name) => {
    setQ((cur) => ({ ...cur, status: name }));
    if (!isNew) save({ status: name }, { stage: true });
  };

  const lowMarginLines = (() => {
    const min = Number(meta.min_margin_pct || 0);
    if (!min) return [];
    return (totals?.lines || [])
      .map((ln) => resolveLine(ln, q.lines || []))
      .filter((l) => {
        const m = lineMarginPct(l);
        return m != null && m < min;
      });
  })();

  const editLine = (line, patch) =>    setQ((cur) => {
      const lines = [...(cur.lines || [])];
      const i = lines.findIndex((e) =>
        line.manual ? e.id && e.id === line.id : e.key && e.key === line.key,
      );
      if (i >= 0) lines[i] = { ...lines[i], ...patch };
      else if (line.manual)
        lines.push({ ...line, manual: true, id: line.id, ...patch });
      else lines.push({ key: line.key, ...patch });
      return { ...cur, lines };
    });

  const resetLine = (line) =>
    setQ((cur) => ({
      ...cur,
      lines: (cur.lines || []).filter((e) => e.key !== line.key),
    }));

  const removeLine = (line) =>
    setQ((cur) => ({
      ...cur,
      lines: line.manual
        ? (cur.lines || []).filter((e) => e.id !== line.id)
        : [...(cur.lines || []), { key: line.key, removed: true }],
    }));

  const matName = (color, thickness) => {
    const c = (color || "").trim();
    const t = (thickness || "").trim();
    if (!t || c.toLowerCase().endsWith(t.toLowerCase())) return c;
    return `${c} ${t}`.trim();
  };

  const catalog = [
    ...services.map((s) => ({
      value: `service:${s.id}`,
      label: `${s.name} · ${money(s.cost_estimate)}/${s.unit}`,
      hint: "Service",
    })),
    ...sinks.map((s) => ({
      value: `sink:${s.id}`,
      label: `${s.label} · ${money(s.price)}${s.qty > 0 ? "" : " · out of stock"}`,
      hint: "Sink",
    })),
    ...slabStock.map((s) => ({
      value: `slab:${s.id}`,
      label: `${matName(s.slab_color, s.thickness)} · ${money(
        s.sale_price_per_sqft ?? meta.rates?.[s.stone_group] ?? 0,
      )}/SF`,
      hint: "Slab",
    })),
    ...remnants.map((r) => ({
      value: `remnant:${r.id}`,
      label: `${matName(r.slab_color, r.thickness)} · ${r.code} · ${money(
        r.sale_price_per_sqft ?? 0,
      )}/SF`,
      hint: "Remnant",
    })),
    ...cabinets.map((c) => ({
      value: `cabinet:${c.id}`,
      label: `${c.code} · ${c.name || c.door_style || "Cabinet"} · ${money(
        c.price || 0,
      )}${c.qty > 0 ? ` · ${c.qty} on hand` : " · out of stock"}`,
      hint: "Cabinet",
      img: c.photo ? photoHref(c.photo) : "",
    })),
  ];

  const materialOptions = [
    ...Object.values(
      slabStock.reduce((acc, s) => {
        const key = `${s.slab_color} ${s.thickness}`;
        if (!acc[key])
          acc[key] = {
            value: `slab|${s.slab_color}|${s.stone_group || ""}|${s.thickness || ""}`,
            label: `${matName(s.slab_color, s.thickness)}${
              s.stone_group ? ` · ${s.stone_group}` : ""
            }`,
            hint: "In stock",
          };
        return acc;
      }, {}),
    ),
    ...remnants.map((r) => ({
      value: `remnant|${r.slab_color}|${""}|${r.thickness || ""}`,
      label: `${matName(r.slab_color, r.thickness)} · ${r.code} · ${
        r.usable_sqft || 0
      } ft²`,
      hint: "Remnant",
    })),
    ...colors
      .filter(
        (c) =>
          !slabStock.some(
            (s) => s.slab_color === c.slab_color && s.thickness === c.thickness,
          ),
      )
      .map((c) => ({
        value: `catalog|${c.slab_color}||${c.thickness || ""}`,
        label: matName(c.slab_color, c.thickness),
        hint: "Catalogue",
      })),
  ];

  const pickAreaMaterial = (areaId, value) => {
    const [kind, color, group] = String(value || "").split("|");
    if (!kind || kind === "catalog" || !color) {
      setArea(areaId, { slab_color: value || "" });
      return;
    }
    setArea(areaId, {
      slab_color: color,
      ...(group ? { stone_group: group } : {}),
    });
  };

  const patchOptions = (areaId, fn) =>
    setQ((cur) => ({
      ...cur,
      areas: cur.areas.map((a) =>
        a.id === areaId ? { ...a, material_options: fn(a.material_options || [], a) } : a,
      ),
    }));

  const addMaterialOption = (areaId) =>
    patchOptions(areaId, (opts) => [
      ...opts,
      { id: uid(), slab_color: "", stone_group: "", unit_price: null, note: "" },
    ]);

  const editMaterialOption = (areaId, optId, patch) =>
    patchOptions(areaId, (opts) =>
      opts.map((o) => {
        if (o.id !== optId) return o;
        if (patch.pick !== undefined) {
          const [kind, color, group] = String(patch.pick || "").split("|");
          return !kind || kind === "catalog" || !color
            ? { ...o, slab_color: patch.pick || "", stone_group: "" }
            : { ...o, slab_color: color, stone_group: group || "" };
        }
        return { ...o, ...patch };
      }),
    );

  const removeMaterialOption = (areaId, optId) =>
    patchOptions(areaId, (opts) => opts.filter((o) => o.id !== optId));

  const swapMaterialOption = (areaId, optId) => {
    setQ((cur) => {
      const area = cur.areas.find((a) => a.id === areaId);
      const opt = (area?.material_options || []).find((o) => o.id === optId);
      const hasPrice = opt && opt.unit_price !== null && opt.unit_price !== "";
      if (!opt || (!opt.slab_color && !hasPrice)) return cur;
      const newColor = opt.slab_color || area.slab_color || "";
      const swapped = {
        ...opt,
        slab_color: area.slab_color || "",
        stone_group: area.stone_group || "",
        unit_price: null,
      };
      const matKeys = [":material", ":drop", ":backsplash"].map((k) => `${areaId}${k}`);
      const price = hasPrice ? Number(opt.unit_price) : null;
      return {
        ...cur,
        lines: (cur.lines || [])
          .filter((e) => e.manual || !matKeys.includes(e.key))
          .map((e) =>
            price !== null && e.manual && e.is_material && (e.area_id || "") === areaId
              ? { ...e, unit_price: price }
              : e,
          )
          .concat(price !== null ? matKeys.map((key) => ({ key, unit_price: price })) : []),
        areas: cur.areas.map((a) =>
          a.id !== areaId
            ? a
            : {
                ...a,
                slab_color: newColor,
                stone_group: opt.stone_group || "",
                material_options: (a.material_options || []).map((o) =>
                  o.id === optId
                    ? swapped
                    : o,
                ).filter((o) => o.slab_color),
              },
        ),
      };
    });
    toast.success("Applied to the quote — material now uses the option's color and price");
  };

  const [colourDialog, setColourDialog] = useState(false);
  const [choiceBusy, setChoiceBusy] = useState(false);

  const openColourDialog = async () => {
    if (!id) {
      toast.error("Save the quote first");
      return;
    }
    if (dirty) await save();
    setColourDialog(true);
  };

  const ackColourChoice = async (choice) => {
    setChoiceBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/colour-choice/ack`, {
        area_id: choice.area_id,
      });
      setQ((cur) => ({ ...cur, colour_choices: data.colour_choices }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not update");
    }
    setChoiceBusy(false);
  };

  const applyColourChoice = async (choice) => {
    swapMaterialOption(choice.area_id, choice.choice_id);
    await ackColourChoice(choice);
  };

  const catalogLine = (value) => {
    const [kind, itemId] = String(value || "").split(":");
    if (kind === "service") {
      const s = services.find((x) => x.id === itemId);
      if (s)
        return {
          service_id: s.id,
          item: s.name,
          description: s.description || s.category || "",
          qty: 1,
          unit: s.unit || "EA",
          unit_price: Number(s.cost_estimate) || 0,
          taxable: !!s.taxable,
        };
    } else if (kind === "sink") {
      const s = sinks.find((x) => x.id === itemId);
      if (s)
        return {
          sink_id: s.id,
          item: s.label,
          description: s.description || [s.sink_type, s.size].filter(Boolean).join(" · "),
          qty: 1,
          unit: "EA",
          unit_price: s.price,
          taxable: true,
        };
    } else if (kind === "slab") {
      const s = slabStock.find((x) => x.id === itemId);
      if (s) {
        const sf =
          Math.round(((s.length_in || 0) * (s.width_in || 0)) / 1.44) / 100;
        return {
          slab_id: s.id,
          item: matName(s.slab_color, s.thickness),
          description: `Slab ${formatInches(s.length_in || 0)} x ${formatInches(s.width_in || 0)}`,
          qty: sf || 1,
          unit: sf ? "SF" : "EA",
          unit_price: Number(
            s.sale_price_per_sqft ?? meta.rates?.[s.stone_group] ?? 0,
          ),
          taxable: true,
        };
      }
    } else if (kind === "remnant") {
      const r = remnants.find((x) => x.id === itemId);
      if (r) {
        const sf =
          Number(r.usable_sqft) ||
          Math.round(((r.length_in || 0) * (r.width_in || 0)) / 1.44) / 100;
        return {
          remnant_id: r.id,
          item: `${matName(r.slab_color, r.thickness)} remnant`.trim(),
          description: `${r.code} · ${formatInches(r.length_in || 0)} x ${formatInches(r.width_in || 0)}`,
          qty: sf || 1,
          unit: "SF",
          unit_price: Number(r.sale_price_per_sqft ?? 0),
          taxable: true,
        };
      }
    } else if (kind === "cabinet") {
      const c = cabinets.find((x) => x.id === itemId);
      if (c)
        return {
          cabinet_id: c.id,
          item: `${c.code} ${c.name || c.door_style || ""}`.trim(),
          description: [
            c.door_style,
            c.category,
            c.width_in || c.height_in || c.depth_in
              ? `${c.width_in || "?"}×${c.height_in || "?"}×${c.depth_in || "?"}"`
              : "",
          ]
            .filter(Boolean)
            .join(" · "),
          qty: 1,
          unit: "EA",
          unit_price: Number(c.price) || 0,
          taxable: true,
        };
    }
    return null;
  };

  const addCatalogItem = (areaId, value) => {
    const line = catalogLine(value);
    if (!line) return;
    setQ((cur) => ({
      ...cur,
      lines: [
        ...(cur.lines || []),
        {
          id: uid(),
          manual: true,
          area_id: areaId || "",
          hidden: false,
          ...line,
        },
      ],
    }));
    toast.success(`${line.item} added`);
  };

  const pickLineItem = (line, value) => {
    const picked = catalogLine(value);
    editLine(line, picked || { item: value });
  };

  const sendNestToQuote = async (line) => {
    const lid = `stoneex-nest-${line.area_id || ""}`;
    const lines = [
      ...(q.lines || []).filter((e) => e.id !== lid),
      { id: lid, manual: true, hidden: false, ...line },
    ];
    setQ((cur) => ({ ...cur, lines }));
    await save({ lines }, { silent: true });
    toast.success("Added to quote");
  };

  const LINE_TYPE_DEFAULTS = {
    material: { item: "Material", unit: "SF", is_material: true, taxable: true },
    service: { item: "Service", unit: "EA", taxable: false },
    cabinet: { item: "Cabinet", unit: "EA", taxable: true },
    sink: { item: "Sink", unit: "EA", taxable: true },
    slab: { item: "Slab", unit: "SF", is_material: true, taxable: true },
    remnant: { item: "Remnant", unit: "SF", is_material: true, taxable: true },
    custom: { item: "New line", unit: "EA", taxable: true },
  };

  const addLine = (areaId, kind = "custom") => {
    const d = LINE_TYPE_DEFAULTS[kind] || LINE_TYPE_DEFAULTS.custom;
    setQ((cur) => ({
      ...cur,
      lines: [
        ...(cur.lines || []),
        {
          id: uid(),
          manual: true,
          area_id: areaId || "",
          item: d.item,
          description: "",
          qty: 1,
          unit: d.unit,
          unit_price: 0,
          taxable: d.taxable,
          is_material: !!d.is_material,
          hidden: false,
        },
      ],
    }));
  };

  const priceKey = JSON.stringify([
    q.areas,
    q.lines,
    q.discount,
    q.discount_pct,
    q.trade_account,
    q.customer_group,
    q.slab_color,
    q.stone_group,
    q.thickness,
  ]);

  useEffect(() => {
    let stale = false;
    const t = setTimeout(() => {
      api
        .post("/quotes/price", q)
        .then(({ data }) => {
          if (!stale) setTotals(data);
        })
        .catch(() => {});
    }, 350);
    return () => {
      stale = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceKey]);

  const movePiece = (pieceId, targetAreaId) =>
    setQ((cur) => {
      const piece = cur.areas
        .flatMap((a) => a.pieces || [])
        .find((p) => p.id === pieceId);
      if (!piece) return cur;
      return {
        ...cur,
        areas: cur.areas.map((a) =>
          a.id === targetAreaId
            ? { ...a, pieces: [...(a.pieces || []), piece] }
            : {
                ...a,
                pieces: (a.pieces || []).filter((p) => p.id !== pieceId),
              },
        ),
      };
    });

  const splitAreaPiece = (areaId, pieceId, axis, at) => {
    layoutHistory.remember();
    setQ((cur) => {
      const area = cur.areas.find((a) => a.id === areaId);
      const piece = (area?.pieces || []).find((p) => p.id === pieceId);
      if (!piece) return cur;
      const halves = splitPiece(piece, axis, at);
      if (!halves) {
        toast.error("That cut misses the piece");
        return cur;
      }
      const layout = cur.layout || {};
      const placements = { ...(layout.placements || {}) };
      const old = placements[pieceId];
      delete placements[pieceId];
      if (old) {
        placements[halves[0].id] = { ...old };
        placements[halves[1].id] = {
          ...old,
          x: axis === 0 ? old.x + at + 1 : old.x,
          y: axis === 1 ? old.y + at + 1 : old.y,
        };
      }
      return {
        ...cur,
        layout: { ...layout, placements },
        areas: cur.areas.map((a) =>
          a.id !== areaId
            ? a
            : {
                ...a,
                pieces: a.pieces.flatMap((p) =>
                  p.id === pieceId ? halves : [p],
                ),
              },
        ),
      };
    });
    toast.success("Seam cut — piece split in two");
  };

  const mergeSeam = (areaId, pieceId) => {
    const next = mergeSeamIn(q, areaId, pieceId);
    if (!next) {
      toast.error("Couldn't weld that seam — the other half isn't lined up with it");
      return;
    }
    layoutHistory.remember();
    setQ(next);
    toast.success("Seam removed — pieces joined back together");
  };

  const setArea = (areaId, patch) =>
    setQ((cur) => ({
      ...cur,
      areas: cur.areas.map((a) => (a.id === areaId ? { ...a, ...patch } : a)),
    }));

  const addArea = () => {
    const id = uid();
    setQ((cur) => ({
      ...cur,
      areas: [
        ...cur.areas,
        {
          id,
          name: `Area ${cur.areas.length + 1}`,
          pieces: [],
          cutouts: [],
        },
      ],
    }));
    setAreaTab(id);
  };

  const removeEmptyArea = (areaId) => {
    const idx = q.areas.findIndex((a) => a.id === areaId);
    if (idx < 0 || q.areas.length < 2) return;
    const removed = q.areas[idx];
    setQ((cur) => ({ ...cur, areas: cur.areas.filter((a) => a.id !== areaId) }));
    toast(`${removed.name || "Area"} removed`, {
      action: {
        label: "Undo",
        onClick: () =>
          setQ((cur) => {
            if (cur.areas.some((a) => a.id === areaId)) return cur;
            const areas = [...cur.areas];
            areas.splice(Math.min(idx, areas.length), 0, removed);
            return { ...cur, areas };
          }),
      },
    });
  };

  const importDxf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/quotes/dxf/preview", fd);
      const area = q.areas[0] || {
        id: uid(),
        name: "Kitchen",
        pieces: [],
        cutouts: [],
      };
      const pieces = data.pieces.map((p) => ({
        id: uid(),
        label: p.label,
        shape: "poly",
        length_in: p.length_in,
        width_in: p.width_in,
        points: p.points,
        edges: p.edges,
        cutouts: p.cutouts || [],
        x: p.x || 0,
        y: p.y || 0,
        backsplash_sqft: 0,
      }));
      setQ((cur) => ({
        ...cur,
        areas: cur.areas.length
          ? cur.areas.map((a, i) =>
              i === 0 ? { ...a, pieces: [...a.pieces, ...pieces] } : a,
            )
          : [{ ...area, pieces }],
      }));
      toast.success(`${pieces.length} pieces read from ${file.name}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not read that file",
      );
    }
    setBusy(false);
  };

  const accept = async () => {
    if (q.links?.job_id) {
      toast.error("This quote already started its project");
      return;
    }
    if (
      !q.customer_id &&
      !window.confirm(
        "This quote isn't linked to an account yet, so the project won't be tied to a customer record. Accept anyway?",
      )
    )
      return;
    const saved = await save({}, { silent: true });
    if (!saved) return;
    const { data } = await api.post(`/quotes/${saved.id}/accept`);
    toast.success(`Quote accepted — field work ${data.job_code || ""} started on the project`);
    navigate(`/projects/${data.job_id}?tab=field`);
  };

  const scheduleFromQuote = async () => {
    if (!schedDate) {
      toast.error("Pick a date first");
      return;
    }
    const saved = await save({}, { silent: true });
    if (!saved) return;
    setSchedBusy(true);
    try {
      const { data } = await api.post(`/quotes/${saved.id}/schedule-job`, {
        job_type: schedType,
        date: schedDate,
        time: schedTime,
        window: schedWindow,
        crew: schedCrew,
      });
      const bits = [];
      if (data.fixtures) bits.push(`${data.fixtures} fixture(s)`);
      if (data.slabs) bits.push(`${data.slabs} slab(s)`);
      toast.success(`Job ${data.code} booked${bits.length ? " with " + bits.join(" and ") : ""}`);
      setScheduleOpen(false);
      navigate(`/projects/${data.job_id}?tab=field`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not schedule the project",
      );
    }
    setSchedBusy(false);
  };

  const unlinkProject = async () => {
    const pid = q.links?.project_id;
    if (!pid) return;
    if (
      !window.confirm(
        `Detach this quote from ${q.links.project_code || "its project"}? You can then accept it into a new project or attach it elsewhere.`,
      )
    )
      return;
    try {
      await api.post(`/projects/${pid}/detach`, { quote_id: id });
      toast.success("Quote detached from the project");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not detach the quote",
      );
    }
  };

  const createProject = async () => {
    if (q.links?.project_id) {
      toast.error("This quote is already linked to a project");
      return;
    }
    const saved = await save({}, { silent: true });
    if (!saved) return;
    try {
      const { data } = await api.post(`/quotes/${saved.id}/create-project`);
      toast.success("Project created from this quote");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not create a project",
      );
    }
  };


  const deleteQuote = async () => {
    if (isNew) return;
    if (
      !window.confirm(
        "Delete this quote permanently? This can't be undone.",
      )
    )
      return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Quote deleted");
      navigate("/quotes");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not delete the quote",
      );
    }
  };

  const setPortalFlag = async (patch) => {
    setQ((cur) => ({ ...cur, ...patch }));
    if (isNew) return;
    try {
      await api.patch(`/quotes/${id}/portal-flags`, patch);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not change that",
      );
    }
  };

  const [waived, setWaived] = useState(false);
  useEffect(() => {
    if (!id || id === "new") return;
    api.get(`/quotes/${id}/approval-waived`).then(({ data }) => setWaived(!!data.waived)).catch(() => {});
  }, [id, q?.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const approveManually = async () => {
    if (q.status === "Accepted") return;
    if (!window.confirm("Accept this quote on the customer's behalf and start the project?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/approve-manual`, { quote: true, drawing: false });
      if (data.job_id) {
        toast.success("Accepted — job created");
        navigate(`/projects/${data.job_id}?tab=field`);
      } else {
        setQ((cur) => ({ ...cur, ...data }));
        toast.success("Quote accepted manually");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not approve");
    }
    setBusy(false);
  };

  // requestApproval retired: drawing sign-off was removed from the workflow.

  const [emailDialog, setEmailDialog] = useState(false);

  const sendEmail = async () => {
    if (yieldInfo && yieldInfo.level !== "ok") {
      const ok = window.confirm(
        `Slab yield check: ${yieldInfo.messages.join(" ")}\n\nSend this quote anyway?`,
      );
      if (!ok) return;
    }
    if (
      !q.customer_id &&
      !window.confirm(
        "This quote isn't linked to an account yet. Send it anyway?",
      )
    )
      return;
    const saved = await save({}, { silent: true });
    if (!saved) return;
    setEmailDialog(true);
  };

  const deleteDrawing = (areaId) => {
    const area = q.areas.find((a) => a.id === areaId);
    if (!area) return;
    if (
      !window.confirm(
        `Delete the drawing for ${area.name || "this area"}? Pieces, walls, notes and the trace go — the area, its material and lines stay.`,
      )
    )
      return;
    setQ((cur) => {
      const gone = new Set((area.pieces || []).map((p) => p.id));
      const layout = cur.layout || {};
      const placements = Object.fromEntries(
        Object.entries(layout.placements || {}).filter(([pid]) => !gone.has(pid)),
      );
      const areas = cur.areas.map((a) =>
        a.id !== areaId
          ? a
          : { ...a, pieces: [], walls: [], notes: [], trace: {} },
      );
      const anyLeft = areas.some((a) => (a.pieces || []).length);
      return {
        ...cur,
        areas,
        layout: { ...layout, placements },
        ...(anyLeft ? {} : { drawing_approval: {} }),
      };
    });
    toast.success("Drawing deleted");
  };

  // Live preview of the exact branded PDF the customer receives, before emailing.
  const openPreview = async () => {
    const saved = await save({}, { silent: true });
    if (!saved) return;
    setPreviewId(saved.id);
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const url = await previewFile(`/quotes/${saved.id}/pdf`);
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
    } catch {
      toast.error("Could not build the PDF preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return "";
    });
  };

  const activeAreaId =
    q.areas.find((a) => a.id === areaTab)?.id || q.areas[0]?.id || null;

  const sqftOf = (p) =>
    p.shape === "poly" && p.points.length > 2
      ? Math.round(
          (Math.abs(
            p.points.reduce((s, [x, y], i) => {
              const [x2, y2] = p.points[(i + 1) % p.points.length];
              return s + x * y2 - x2 * y;
            }, 0) / 2,
          ) /
            144) *
            100,
        ) / 100
      : Math.round(((p.length_in * p.width_in) / 144) * 100) / 100;

  return (
    <div className="grain min-h-screen">
      <AppHeader title={isNew ? "New quote" : q.code} />
      {!isNew && q.customer_id && (
        <AssistantDock
          context={{
            kind: "customer",
            id: q.customer_id,
            label: q.customer_name || q.code,
            quote_id: q.id,
            quote_code: q.code,
            prompts: [
              `Schedule a template for ${q.customer_name} on `,
              `Schedule an install for ${q.customer_name} on `,
              `Add a reminder task to follow up on quote ${q.code} with ${q.customer_name}`,
            ],
          }}
        />
      )}
      <ColourOptionsDialog
        open={colourDialog}
        onOpenChange={setColourDialog}
        quoteId={id}
        defaultTo={q.customer_email || ""}
      />
      <QuoteStockDialog
        quoteId={id}
        open={stockOpen}
        onClose={() => setStockOpen(false)}
        onChanged={() => {
          skipReloadRef.current = null;
          load();
          api.get(`/quotes/${id}/stock`).then(({ data }) => setStockAreas(data.areas || [])).catch(() => {});
        }}
      />
      <QuoteEmailDialog
        open={emailDialog}
        onOpenChange={setEmailDialog}
        quoteId={id}
        onSent={() => load()}
      />
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent
          data-testid="schedule-from-quote-dialog"
          className="border-zinc-800 bg-zinc-900 text-zinc-100"
        >
          <DialogHeader>
            <DialogTitle>Schedule this project from the quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-zinc-400">
              Books field work for {q.customer_name || "this customer"} and carries over the rooms,
              pieces, cutouts and reserved slabs from quote {q.code}.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Stage</Label>
                <Select value={schedType} onValueChange={setSchedType}>
                  <SelectTrigger data-testid="schedule-stage" className="mt-1 bg-zinc-950">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Template">Template</SelectItem>
                    <SelectItem value="Fabrication">Fabrication</SelectItem>
                    <SelectItem value="Install">Install</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <div className="mt-1">
                  <DateField
                    value={schedDate}
                    onChange={setSchedDate}
                    testId="schedule-date"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Time (optional)</Label>
              <div className="mt-1">
                <TimeField
                  value={schedTime}
                  onChange={setSchedTime}
                  testId="schedule-time"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Arrival window (optional)</Label>
              <Select value={schedWindow} onValueChange={setSchedWindow}>
                <SelectTrigger data-testid="schedule-window" className="mt-1 bg-zinc-950">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ARRIVAL_WINDOWS.map((w) => (
                    <SelectItem key={w} value={w}>
                      {w}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Crew (optional)</Label>
              <Input
                data-testid="schedule-crew"
                value={schedCrew}
                placeholder="e.g. Install Team A"
                onChange={(e) => setSchedCrew(e.target.value)}
                className="mt-1 bg-zinc-950"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setScheduleOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="schedule-from-quote-confirm"
              onClick={scheduleFromQuote}
              disabled={schedBusy || !schedDate}
              className="bg-[#c39e6d] font-bold text-[#1a1a18] hover:bg-[#b0895a]"
            >
              {schedBusy ? "Booking\u2026" : "Book job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={previewOpen}
        onOpenChange={(o) => (o ? setPreviewOpen(true) : closePreview())}
      >
        <DialogContent
          data-testid="quote-preview-dialog"
          className="max-w-4xl border-zinc-800 bg-zinc-950 p-0"
        >
          <DialogHeader className="border-b border-zinc-800 px-5 py-3">
            <DialogTitle className="text-sm font-bold uppercase tracking-widest text-zinc-200">
              Quote PDF preview
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              This is exactly what the customer receives when you email the quote.
            </DialogDescription>
          </DialogHeader>
          <div className="h-[70vh] bg-zinc-900">
            {previewLoading && (
              <div
                data-testid="quote-preview-loading"
                className="flex h-full items-center justify-center text-xs uppercase tracking-widest text-zinc-500"
              >
                Building preview…
              </div>
            )}
            {!previewLoading && previewUrl && (
              <iframe
                title="Quote PDF preview"
                data-testid="quote-preview-frame"
                src={previewUrl}
                className="h-full w-full border-0"
              />
            )}
          </div>
          <DialogFooter className="gap-2 border-t border-zinc-800 px-5 py-3">
            <Button
              data-testid="quote-preview-download"
              variant="outline"
              disabled={!previewUrl}
              onClick={() =>
                downloadFile(
                  `/quotes/${previewId || id}/pdf`,
                  `Quote-${q.code || "draft"}.pdf`,
                ).catch(() => toast.error("Could not build the PDF"))
              }
              className="h-10 border-zinc-800 bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              <FilePdf size={15} weight="bold" className="mr-2" /> Download
            </Button>
            <Button
              data-testid="quote-preview-send"
              onClick={() => {
                closePreview();
                sendEmail();
              }}
              className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <PaperPlaneTilt size={15} weight="bold" className="mr-2" /> Email
              quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <main className="relative z-10 pb-28 pt-6">
        <button
          type="button"
          data-testid="quote-back"
          onClick={() => {
            if (
              dirty &&
              !window.confirm("You have unsaved changes. Leave without saving?")
            )
              return;
            navigate("/quotes");
          }}
          className="no-print inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> All quotes
        </button>

        <div className="mt-3 min-w-0">
          <div className="flex items-center gap-2">
            {!isNew && (
              <span className="font-mono-data text-[11px] text-indigo-300">
                {q.code}
              </span>
            )}
            {!isNew && (
              <span
                data-testid="quote-status-dot"
                className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-300"
              >
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    {
                      Draft: "bg-zinc-400",
                      Sent: "bg-blue-400",
                      Accepted: "bg-emerald-400",
                      Declined: "bg-red-400",
                      Expired: "bg-amber-400",
                    }[q.status] || "bg-indigo-400"
                  }`}
                />
                {q.status}
              </span>
            )}
            {!isNew && (
              <DrawingApprovalBadge
                approval={approval}
                testid="quote-drawing-approval-badge"
              />
            )}
            {!isNew && (
              <HoldBadge status={q.hold_status} since={q.hold_since} note={q.hold_note} testId="quote-hold-badge" />
            )}
          </div>
          <h1
            className="truncate text-2xl font-bold tracking-tight"
            data-testid="quote-project-title"
          >
            {q.project_name || "New quote"}
          </h1>
          {!isNew && (
            <DrawingApprovalTimeline
              log={q.drawing_approval_log}
              testid="quote-drawing-approval-timeline"
            />
          )}
          {approval.status === "change_requested" && (
            <div
              data-testid="quote-reprice-flag"
              className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
            >
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-400" />
              <span>
                <strong className="font-semibold">Change requested</strong> — this may affect
                square footage or price. Review the drawing and re-quote before sending.
              </span>
            </div>
          )}
        </div>

        <div className="no-print mt-4" data-testid="quote-steps">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex w-full min-w-0 items-center gap-1 overflow-x-auto rounded-lg border border-white/[0.07] bg-zinc-900/70 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl lg:w-auto">
              {STEPS.map((s, i) => {
                const active = s.step != null && step === s.step;
                const done = s.step != null && step > s.step;
                const Icon = s.icon;
                return (
                  <button
                    key={s.label}
                    type="button"
                    data-testid={`quote-step-${i}`}
                    onClick={async () => {
                      if (s.action === "design") {
                        if (!isNew) return navigate(`/quotes/${id}/design`);
                        const saved = await save({}, { silent: true });
                        if (saved) navigate(`/quotes/${saved.id}/design`);
                        return;
                      }
                      setStep(s.step);
                    }}
                    className={`relative flex flex-1 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 py-3 text-sm font-semibold tracking-tight transition-all duration-200 sm:text-base ${
                      active
                        ? "bg-white/[0.11] text-white ring-1 ring-white/15 shadow-[0_10px_24px_-12px_rgba(0,0,0,0.9)]"
                        : done
                          ? "text-indigo-200/70 hover:bg-white/[0.05] hover:text-white"
                          : "text-zinc-500 hover:bg-white/[0.04] hover:text-zinc-200"
                    }`}
                  >
                    {done ? (
                      <Check
                        size={16}
                        weight="bold"
                        className="text-indigo-300"
                      />
                    ) : (
                      <Icon
                        size={17}
                        weight="bold"
                        className={active ? "text-indigo-300" : "opacity-70"}
                      />
                    )}
                    {s.label}
                  </button>
                );
              })}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-1 rounded-lg border border-white/[0.07] bg-zinc-900/70 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl">
              <span
                data-testid="quote-autosave-status"
                className="flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider"
              >
                {saveState === "saving" ? (
                  <>
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" />
                    <span className="text-amber-300">Saving…</span>
                  </>
                ) : saveState === "error" ? (
                  <>
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                    <span className="text-red-400">Save failed</span>
                  </>
                ) : dirty ? (
                  <>
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-300" />
                    <span className="text-amber-300">Unsaved changes</span>
                  </>
                ) : (
                  <>
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    <span className="text-emerald-400">Saved</span>
                  </>
                )}
              </span>
              <Button
                type="button"
                data-testid="quote-save-button"
                onClick={() => save()}
                disabled={busy || (!dirty && !isNew)}
                className="h-12 rounded-full bg-emerald-500 px-5 text-xs font-bold uppercase tracking-widest text-white hover:bg-emerald-400 disabled:opacity-40"
              >
                {busy ? "Saving…" : isNew ? "Create quote" : "Save"}
              </Button>
              <Select
                value={q.status || "Draft"}
                onValueChange={async (v) => {
                  if (!v.startsWith("hold:")) {
                    setStage(v);
                    return;
                  }
                  const hold_status = v.slice(5);
                  try {
                    const { data } = await api.patch(`/quotes/${q.id}/hold`, {
                      hold_status,
                      hold_note: hold_status ? q.hold_note || "" : "",
                    });
                    setQ((cur) => ({ ...cur, hold_status: data.hold_status, hold_note: data.hold_note, hold_since: data.hold_since }));
                    toast.success(hold_status ? `Marked ${hold_status}` : "Hold cleared");
                  } catch (e) {
                    toast.error("Could not update the hold");
                  }
                }}
              >
                <SelectTrigger
                  data-testid="quote-stage-select"
                  className={`h-12 w-44 rounded-full border-0 text-xs font-semibold uppercase tracking-widest hover:bg-white/[0.1] ${
                    q.hold_status
                      ? "bg-amber-500/15 text-amber-200"
                      : "bg-white/[0.06] text-zinc-200"
                  }`}
                >
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Stage
                    </SelectLabel>
                    {(stages.length
                      ? stages
                      : [{ name: "Draft" }, { name: "Sent" }]
                    ).map((s) => (
                      <SelectItem key={s.name} value={s.name}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  {!isNew && (
                    <>
                      <SelectSeparator />
                      <SelectGroup>
                        <SelectLabel className="text-[10px] uppercase tracking-widest text-zinc-500">
                          Waiting
                        </SelectLabel>
                        <SelectItem
                          value="hold:"
                          data-testid="quote-hold-none"
                          className={!q.hold_status ? "text-emerald-300" : ""}
                        >
                          Not waiting
                        </SelectItem>
                        {HOLDS.map((h) => (
                          <SelectItem
                            key={h}
                            value={`hold:${h}`}
                            data-testid={`quote-hold-${h.toLowerCase().replace(/\s+/g, "-")}`}
                            className={q.hold_status === h ? "text-amber-300" : ""}
                          >
                            {q.hold_status === h ? "✓ " : ""}{h}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </>
                  )}
                </SelectContent>
              </Select>
              {!isNew && q.hold_status && (
                <input
                  data-testid="quote-hold-note"
                  defaultValue={q.hold_note || ""}
                  key={`${q.hold_status}-${q.hold_note || ""}`}
                  placeholder={`${q.hold_status} — what for?`}
                  disabled={busy}
                  onBlur={async (e) => {
                    if (e.target.value === (q.hold_note || "")) return;
                    try {
                      const { data } = await api.patch(`/quotes/${q.id}/hold`, {
                        hold_status: q.hold_status,
                        hold_note: e.target.value,
                      });
                      setQ((cur) => ({ ...cur, hold_note: data.hold_note }));
                    } catch {
                      toast.error("Could not save the note");
                    }
                  }}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  className="h-12 w-56 rounded-full border border-amber-500/30 bg-zinc-950 px-4 text-xs text-zinc-200 outline-none placeholder:text-zinc-600 focus:border-amber-500/60"
                />
              )}
              {!isNew && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="quote-actions-menu"
                      title="Quote actions"
                      className="flex h-12 w-12 items-center justify-center rounded-full border border-white/[0.07] bg-zinc-900/70 text-zinc-300 backdrop-blur-xl transition-colors duration-200 hover:bg-white/[0.08]"
                    >
                      <DotsThreeVertical size={20} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    data-testid="quote-actions-menu-content"
                    className="w-56 border-zinc-800 bg-zinc-900"
                  >
                    <DropdownMenuItem
                      data-testid="menu-quote-print"
                      onClick={() => {
                        setStep(4);
                        setTimeout(() => window.print(), 400);
                      }}
                    >
                      <Printer size={15} weight="bold" className="mr-2" /> Print
                      / PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="menu-quote-preview-pdf"
                      onClick={openPreview}
                    >
                      <FilePdf size={15} weight="bold" className="mr-2" />{" "}
                      Preview PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="menu-quote-recalc"
                      onClick={() => save({}, { silent: true })}
                    >
                      <ArrowsClockwise size={15} weight="bold" className="mr-2" /> Recalculate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="menu-quote-download-pdf"
                      onClick={async () => {
                        const saved = await save({}, { silent: true });
                        if (!saved) return;
                        downloadFile(
                          `/quotes/${saved.id}/pdf`,
                          `Quote-${saved.code || q.code || "draft"}.pdf`,
                        ).catch(() => toast.error("Could not build the PDF"));
                      }}
                    >
                      <FilePdf size={15} weight="bold" className="mr-2" />{" "}
                      Download PDF
                    </DropdownMenuItem>
                    {!isNew && q.customer_id && (
                      <DropdownMenuItem
                        data-testid="menu-quote-move-account"
                        onClick={() => setMoveOpen(true)}
                      >
                        <ArrowsClockwise size={15} weight="bold" className="mr-2" /> Move to another account
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      data-testid="menu-quote-email"
                      onClick={sendEmail}
                    >
                      <PaperPlaneTilt
                        size={15}
                        weight="bold"
                        className="mr-2"
                      />{" "}
                      Email quote
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="menu-quote-accept"
                      onClick={accept}
                    >
                      <Check size={15} weight="bold" className="mr-2" /> Mark
                      accepted
                    </DropdownMenuItem>
                    {!isNew && q.customer_id && (
                      <DropdownMenuItem
                        data-testid="menu-quote-schedule-job"
                        onClick={() => {
                          setSchedType("Install");
                          setSchedDate("");
                          setSchedCrew("");
                          setScheduleOpen(true);
                        }}
                      >
                        <CalendarPlus size={15} weight="bold" className="mr-2" /> Schedule
                        from quote
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      data-testid="menu-quote-create-project"
                      disabled={!!q.links?.project_id}
                      onClick={createProject}
                    >
                      <Stack size={15} weight="bold" className="mr-2" /> Create
                      project
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="menu-quote-review"
                      onClick={() => setStep(4)}
                    >
                      <CheckCircle size={15} weight="bold" className="mr-2" />{" "}
                      Go to review
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="menu-quote-delete"
                      onClick={deleteQuote}
                      className="text-red-400 focus:text-red-400"
                    >
                      <Trash size={15} weight="bold" className="mr-2" /> Delete
                      quote
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          <p className="mt-2 pl-2 text-xs text-zinc-500">
            {STEPS.find((s) => s.step === step)?.hint || ""}
          </p>
        </div>

        {step === 0 && (
          <div className="mt-5 space-y-4" data-testid="quote-step-project">
            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
              <h3 className="mb-4 text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                Project details
              </h3>
              <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Project name
              </Label>
              <Input
                data-testid="quote-project-input"
                value={q.project_name}
                onChange={(e) => setQ({ ...q, project_name: e.target.value })}
                placeholder="Elm St kitchen"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Account
              </Label>
              <SearchSelect
                testId="quote-account-select"
                value={q.customer_id || ""}
                placeholder="Pick an account"
                searchPlaceholder="Search accounts…"
                emptyText="No account matches — add a new one"
                addNewLabel="New account"
                onAddNew={() => setNewAcc({ ...EMPTY_ACCOUNT })}
                options={[
                  { value: "", label: "No account yet" },
                  ...accounts.map((a) => ({
                    value: a.id,
                    label: a.company ? `${a.name} · ${a.company}` : a.name,
                    hint: a.group && a.group !== "Regular" ? a.group : "",
                  })),
                ]}
                onChange={(v) => {
                  const acc = accounts.find((a) => a.id === v);
                  setQ({
                    ...q,
                    customer_id: v || null,
                    customer_name: acc?.name || "",
                    customer_email: "",
                    customer_phone: "",
                    customer_address: "",
                    contact_email: "",
                    contact_name: "",
                  });
                  loadAccount(v || null, true);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Customer email (for sending)
              </Label>
              {account ? (
                <SearchSelect
                  testId="quote-email-select"
                  value={q.customer_email || ""}
                  allowCustom
                  placeholder="Pick or type an email"
                  searchPlaceholder="Search or type an email…"
                  emptyText="Type a full email to use it"
                  addNewLabel="New contact"
                  onAddNew={() =>
                    setNewContact({ name: "", role: "", phone: "", email: "" })
                  }
                  options={[{ value: "", label: "No email" }, ...accountEmails]}
                  onChange={(v) => {
                    const ct = (account?.contacts || []).find((c) => c.email === v);
                    setQ({
                      ...q,
                      customer_email: v,
                      contact_email: v,
                      contact_name: ct ? ct.name : q.contact_name,
                    });
                  }}
                />
              ) : (
                <Input
                  data-testid="quote-email-input"
                  value={q.customer_email}
                  onChange={(e) =>
                    setQ({ ...q, customer_email: e.target.value })
                  }
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Slab color
              </Label>
              <SearchSelect
                testId="quote-color-select"
                value={q.slab_color || ""}
                allowCustom
                placeholder="Pick or type a color"
                searchPlaceholder="Search colors…"
                emptyText="Type a color name to use it"
                addNewLabel="New slab color"
                onAddNew={() => setNewSlab({ ...EMPTY_SLAB })}
                options={[
                  { value: "", label: "Not chosen yet" },
                  ...colors.map((c) => ({
                    value: c.slab_color,
                    label: c.slab_color,
                    hint: c.thickness || "",
                  })),
                ]}
                onChange={(v) => setQ({ ...q, slab_color: v })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Working with (Attn)
              </Label>
              {account ? (
                <SearchSelect
                  testId="quote-contact-select"
                  value={q.contact_name || ""}
                  placeholder="Pick a contact"
                  searchPlaceholder="Search contacts…"
                  emptyText="No contact matches — add a new one"
                  addNewLabel="New contact"
                  onAddNew={() =>
                    setNewContact({ name: "", role: "", phone: "", email: "" })
                  }
                  options={[
                    { value: "", label: "No one specified" },
                    ...(account.contacts || [])
                      .filter((ct) => ct.name)
                      .map((ct) => ({
                        value: ct.name,
                        label: ct.name,
                        hint: ct.role || "",
                      })),
                  ]}
                  onChange={(v) => setQ({ ...q, contact_name: v })}
                />
              ) : (
                <Input
                  data-testid="quote-contact-input"
                  value={q.contact_name || ""}
                  onChange={(e) => setQ({ ...q, contact_name: e.target.value })}
                  placeholder="Site contact"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Job address
              </Label>
              <AddressCombo
                testId="quote-job-address"
                value={q.job_address || ""}
                onChange={(v) => setQ({ ...q, job_address: v })}
                options={[account?.address, ...(account?.job_addresses || [])]}
              />
            </div>
              </div>
            </section>
            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
              <h3 className="mb-4 text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                Payment &amp; terms
              </h3>
              <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Payment
              </Label>
              <Select
                value={q.pay_plan || "account"}
                onValueChange={(v) =>
                  setQ({ ...q, pay_plan: v === "account" ? "" : v })
                }
              >
                <SelectTrigger
                  data-testid="quote-pay-plan"
                  className="h-12 border-zinc-800 bg-zinc-950"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="account">Follow account default</SelectItem>
                  <SelectItem value="deposit">50 / 50 — deposit slip + balance</SelectItem>
                  <SelectItem value="full">Full invoice — one payment</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500">
                Sets how invoices are raised when this quote is accepted.
              </p>
            </div>
            {account && (
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Working with account
                </Label>
                <div className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3">
                  <Switch
                    data-testid="quote-trade-toggle"
                    checked={!!q.trade_account}
                    onCheckedChange={(v) => setQ({ ...q, trade_account: v })}
                  />
                  <span
                    className="text-sm text-zinc-300"
                    data-testid="quote-trade-label"
                  >
                    Trade account · {accGroup || "Regular"}
                    {tradePct > 0
                      ? ` — ${tradePct}% discount applied`
                      : " — no discount set for this group (Settings → Pricing)"}
                  </span>
                </div>
              </div>
            )}
              </div>
            </section>
            <div className="flex justify-end">
              <Button
                data-testid="project-next-step"
                onClick={async () => {
                  if (!isNew) return navigate(`/quotes/${id}/design`);
                  const saved = await save({}, { silent: true });
                  if (saved) navigate(`/quotes/${saved.id}/design`);
                }}
                className="h-11 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                Next: Design Studio
                <ArrowRight size={14} weight="bold" className="ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-5 space-y-4" data-testid="quote-step-areas">
            <YieldBanner info={yieldInfo} />
            <div
              className="flex flex-wrap items-center gap-1 rounded-full border border-white/[0.07] bg-zinc-900/70 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl lg:w-fit"
              data-testid="area-tabs"
            >
              {q.areas.map((a) => {
                const on = a.id === activeAreaId;
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`area-tab-${a.id}`}
                    onClick={() => setAreaTab(a.id)}
                    className={`flex h-11 items-center gap-2 rounded-full px-5 text-sm font-semibold tracking-tight transition-all duration-200 ${
                      on
                        ? "bg-white/[0.11] text-white ring-1 ring-white/15"
                        : "text-zinc-500 hover:bg-white/[0.05] hover:text-zinc-200"
                    }`}
                  >
                    {a.name || "Area"}
                    <span className="font-mono-data text-[10px] text-zinc-500">
                      {a.pieces.reduce((n, p) => n + sqftOf(p), 0).toFixed(1)}{" "}
                      ft²
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                data-testid="add-area-tab"
                onClick={addArea}
                className="flex h-11 items-center gap-1.5 rounded-full px-4 text-sm font-semibold text-indigo-300 transition-all duration-200 hover:bg-indigo-500/15"
              >
                <Plus size={15} weight="bold" /> Area
              </button>
            </div>

            {q.areas
              .filter((area) => area.id === activeAreaId)
              .map((area) => {
                const ai = q.areas.findIndex((a) => a.id === area.id);
                return (
                  <div
                    key={area.id}
                    data-testid={`quote-area-${area.id}`}
                    className={
                      fsArea === area.id
                        ? "fixed inset-0 z-[60] overflow-auto bg-zinc-950 p-4"
                        : "rounded-lg border border-zinc-800 bg-zinc-900 p-4"
                    }
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        data-testid={`area-name-${area.id}`}
                        value={area.name}
                        onChange={(e) =>
                          setArea(area.id, { name: e.target.value })
                        }
                        className="h-11 w-52 border-zinc-800 bg-zinc-950 font-semibold"
                      />
                      <span className="font-mono-data text-xs text-zinc-500">
                        {area.pieces
                          .reduce((n, p) => n + sqftOf(p), 0)
                          .toFixed(2)}{" "}
                        ft²
                      </span>
                      <Input
                        data-testid={`area-address-${area.id}`}
                        value={area.address || ""}
                        onChange={(e) =>
                          setArea(area.id, { address: e.target.value })
                        }
                        list="quote-job-addresses"
                        placeholder={q.job_address || "Address (optional)"}
                        className="h-11 w-full border-zinc-800 bg-zinc-950 text-xs sm:w-64"
                      />
                      <div className="ml-auto flex gap-2">
                        <Button
                          data-testid={`area-fullscreen-${area.id}`}
                          variant="outline"
                          onClick={() =>
                            setFsArea(fsArea === area.id ? null : area.id)
                          }
                          className="h-10 border-zinc-700 bg-transparent text-xs text-zinc-300 hover:bg-zinc-800"
                          title={
                            fsArea === area.id
                              ? "Exit full screen"
                              : "Full screen"
                          }
                        >
                          {fsArea === area.id ? (
                            <ArrowsIn size={14} weight="bold" />
                          ) : (
                            <ArrowsOutSimple size={14} weight="bold" />
                          )}
                        </Button>
                        <Button
                          data-testid={`delete-drawing-${area.id}`}
                          variant="outline"
                          disabled={
                            !(area.pieces || []).length &&
                            !(area.walls || []).length &&
                            !(area.notes || []).length &&
                            !area.trace?.path
                          }
                          onClick={() => deleteDrawing(area.id)}
                          title="Delete the drawing (keeps the area, material and lines)"
                          className="h-10 border-zinc-700 bg-transparent text-xs text-amber-300 hover:bg-zinc-800"
                        >
                          <Eraser size={14} weight="bold" />
                          <span className="ml-1.5 hidden sm:inline">Delete drawing</span>
                        </Button>
                        <Button
                          data-testid={`remove-area-${area.id}`}
                          variant="outline"
                          onClick={() =>
                            setQ({
                              ...q,
                              areas: q.areas.filter((a) => a.id !== area.id),
                            })
                          }
                          className="h-10 border-zinc-700 bg-transparent text-xs text-red-400 hover:bg-zinc-800"
                        >
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3">
                      <CountertopDesigner
                        onSendToQuote={sendNestToQuote}
                        pieces={area.pieces}
                        edgeProfiles={meta.edge_profiles}
                        edgeStyles={meta.edge_styles}
                        general={meta.drawing_general}
                        cutoutKits={meta.drawing_kits}
                        onChange={(pieces) => setArea(area.id, { pieces })}
                        areaName={area.name}
                        onAreaName={(name) => setArea(area.id, { name })}
                        areas={q.areas.map((a) => ({ id: a.id, name: a.name }))}
                        currentAreaId={area.id}
                        onMovePiece={movePiece}
                        cutoutKinds={meta.cutout_kinds || []}
                        products={sinks}
                        services={services}
                        cutoutTemplates={cutTemplates}
                        onRenameCutoutTemplate={(tid, name) =>
                          putCutTemplates(
                            cutTemplates.map((x) => (x.id === tid ? { ...x, name } : x)),
                            "Template renamed",
                          )
                        }
                        onSetCutoutTemplatePhoto={(tid, photo) =>
                          putCutTemplates(cutTemplates.map((x) => (x.id === tid ? { ...x, photo } : x)))
                        }
                        customLayouts={customLayouts}
                        onSaveCustomLayout={(l) => putCustomLayouts([...customLayouts, l], `${l.label} added to layouts`)}
                        onDeleteCustomLayout={(lid) => putCustomLayouts(customLayouts.filter((x) => x.id !== lid), "Layout tile removed")}
                        savedLayouts={savedLayouts}
                        liveTotal={totals?.total}
                        expectedRate={totals?.group_rate}
                        billedSqft={totals?.billed_sqft}
                        rateBand={rateBands?.[q?.stone_group]}
                        onSaveLayout={(l) =>
                          putLayouts([...savedLayouts, l], `${l.name} saved`)
                        }
                        onDeleteLayout={(lid) =>
                          putLayouts(
                            savedLayouts.filter((x) => x.id !== lid),
                            "Layout removed",
                          )
                        }
                        onRenameLayout={(lid, name) =>
                          putLayouts(
                            savedLayouts.map((x) =>
                              x.id === lid ? { ...x, name } : x,
                            ),
                            "Layout renamed",
                          )
                        }
                        walls={area.walls || []}
                        onWallsChange={(w) => setArea(area.id, { walls: w })}
                        notes={area.notes || []}
                        onNotesChange={(n) => setArea(area.id, { notes: n })}
                        trace={area.trace?.path ? area.trace : null}
                        onTraceChange={(t) =>
                          setArea(area.id, { trace: t || {} })
                        }
                        onUploadTrace={async (file) => {
                          try {
                            return await uploadTracePhoto(file);
                          } catch {
                            toast.error("Could not upload that picture");
                            return null;
                          }
                        }}
                        canvasHeight={fsArea === area.id ? "76vh" : "58vh"}
                        onSaveCutoutTemplate={(t) =>
                          putCutTemplates(
                            [...cutTemplates, t],
                            `${t.name} saved`,
                          )
                        }
                        onDeleteCutoutTemplate={(id) =>
                          putCutTemplates(
                            cutTemplates.filter((x) => x.id !== id),
                            "Setup removed",
                          )
                        }
                        layoutBoard={
                          <SlabLayoutBoard
                            areas={q.areas}
                            thickness={q.thickness}
                            quoteColor={q.slab_color}
                            layout={q.layout || {}}
                            onLayoutChange={(l) =>
                              setQ((cur) => ({ ...cur, layout: l }))
                            }
                            onBeforeChange={layoutHistory.remember}
                            onUndo={layoutHistory.undo}
                            undoCount={layoutHistory.undoCount}
                            onMergeSeam={mergeSeam}
                            materialOptions={materialOptions}
                            onAreaMaterial={pickAreaMaterial}
                            onAddArea={addArea}
                            onSplitPiece={splitAreaPiece}
                            rateFor={(color) => {
                              const a = q.areas.find(
                                (x) => (x.slab_color || q.slab_color) === color,
                              );
                              const g = a?.stone_group || q.stone_group;
                              return Number(meta.rates?.[g] || 0);
                            }}
                            inventorySlabs={slabStock}
                            quoteId={isNew ? "" : id}
                            onSave={() => save()}
                            saving={busy}
                            onVeinMatch={
                              isNew
                                ? null
                                : () => navigate(`/quotes/${id}/vein`)
                            }
                            portalShown={q.show_layout_in_portal !== false}
                            onPortalShown={(v) =>
                              setPortalFlag({ show_layout_in_portal: v })
                            }
                          />
                        }
                      />

                      <div className="mt-4 flex justify-end">
                        <Button
                          data-testid={`area-next-step-${area.id}`}
                          onClick={() => setStep(2)}
                          className="h-11 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                        >
                          Next step
                          <ArrowRight
                            size={14}
                            weight="bold"
                            className="ml-1.5"
                          />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {step === 2 && (
          <div className="mt-5 space-y-4" data-testid="quote-step-vein">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6">
              <h2 className="text-base font-semibold text-zinc-100 md:text-lg">
                Vein match the slabs
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-zinc-400">
                See how the vein runs through every seam: the pieces are drawn
                as they will be installed, filled with the real slab photo from
                the spot they are cut from. Slide a piece on the slab and the
                preview moves with it.
              </p>
              <div className="mt-5 flex w-fit flex-wrap items-center gap-1 rounded-full border border-white/[0.07] bg-zinc-950/70 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-xl">
                {isNew ? (
                  <button
                    type="button"
                    data-testid="vein-save-first"
                    disabled={busy}
                    onClick={() => save()}
                    className="flex h-10 items-center gap-2 rounded-full bg-white/[0.11] px-5 text-sm font-semibold text-white ring-1 ring-white/15 transition-all duration-200 hover:bg-white/[0.16] disabled:opacity-40"
                  >
                    <FloppyDisk size={16} weight="bold" />
                    Save the quote to vein match
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="open-vein-match"
                      onClick={() => navigate(`/quotes/${id}/vein`)}
                      className="flex h-10 items-center gap-2 rounded-full bg-white/[0.11] px-5 text-sm font-semibold text-white ring-1 ring-white/15 transition-all duration-200 hover:bg-white/[0.16]"
                    >
                      <ImageSquare size={16} weight="bold" />
                      Open vein match
                    </button>
                    <button
                      type="button"
                      data-testid="open-slab-layout-screen"
                      onClick={() => navigate(`/quotes/${id}/layout`)}
                      className="flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-zinc-400 transition-all duration-200 hover:bg-white/[0.06] hover:text-white"
                    >
                      <Stack size={16} weight="bold" />
                      Slab layout screen
                    </button>
                  </>
                )}
                <button
                  type="button"
                  data-testid="vein-back-to-layout"
                  onClick={() => setStep(1)}
                  className="flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-zinc-400 transition-all duration-200 hover:bg-white/[0.06] hover:text-white"
                >
                  <ArrowLeft size={16} weight="bold" />
                  Back to drawing
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                data-testid="vein-next-step"
                onClick={() => setStep(3)}
                className="h-11 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                Next step
                <ArrowRight size={14} weight="bold" className="ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-5 space-y-4" data-testid="quote-step-quote">
            <YieldBanner info={yieldInfo} />
            <ColourChoiceBanner
              choices={q.colour_choices || []}
              busy={choiceBusy}
              onApply={applyColourChoice}
              onDismiss={ackColourChoice}
            />
            {(q.colour_decisions || []).some((d) => d.decision === "decline") && (
              <div
                data-testid="colour-declined-strip"
                className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-xs text-zinc-400"
              >
                <span className="font-semibold uppercase tracking-widest text-zinc-500">Customer declined</span>
                {(q.colour_decisions || [])
                  .filter((d) => d.decision === "decline")
                  .map((d) => (
                    <span key={`${d.area_id}-${d.choice_id}`} className="rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-red-200 line-through">
                      {d.area_name}: {d.slab_color}
                    </span>
                  ))}
              </div>
            )}
            <QuoteLines
              quoteId={isNew ? "" : id}
              totals={totals}
              edits={q.lines || []}
              areas={q.areas}
              services={catalog}
              minMargin={meta.min_margin_pct || 0}
              showProfit={meta.show_profit_strip}
              discount={q.discount}
              busy={busy}
              onDiscount={(v) => setQ((cur) => ({ ...cur, discount: v }))}
              discountPct={q.discount_pct || 0}
              onDiscountPct={(v) =>
                setQ((cur) => ({ ...cur, discount_pct: v }))
              }
              notes={q.notes}
              onNotes={(v) => setQ((cur) => ({ ...cur, notes: v }))}
              onEdit={editLine}
              onReset={resetLine}
              onRemove={removeLine}
              onAdd={addLine}
              onAddService={addCatalogItem}
              onPickItem={pickLineItem}
              materials={materialOptions}
              onAddOption={addMaterialOption}
              onEditOption={editMaterialOption}
              onRemoveOption={removeMaterialOption}
              onUseOption={swapMaterialOption}
              onEmailOptions={openColourDialog}
              onAddArea={addArea}
              onRemoveArea={removeEmptyArea}
              onRenameArea={(areaId, name) =>
                setQ((cur) => ({
                  ...cur,
                  areas: (cur.areas || []).map((a) =>
                    a.id === areaId ? { ...a, name } : a,
                  ),
                }))
              }
              onSave={() => save()}
              onReview={async () => {
                await save();
                setStep(4);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              removedCount={(q.lines || []).filter((e) => e.removed).length}
              onRestoreRemoved={() =>
                setQ((cur) => ({
                  ...cur,
                  lines: (cur.lines || []).filter((e) => !e.removed),
                }))
              }
            />
          </div>
        )}

        {step === 4 && (
          <div className="mt-5" data-testid="quote-step-review">
            <div className="no-print mb-4">
              <YieldBanner info={yieldInfo} />
            </div>
            {lowMarginLines.length > 0 && (
              <div
                className="no-print mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-xs text-red-200"
                data-testid="review-low-margin-banner"
              >
                <div className="font-bold uppercase tracking-widest">
                  Low margin — check before this goes out
                </div>
                <div className="mt-1">
                  {lowMarginLines.length} line{lowMarginLines.length > 1 ? "s" : ""} under your{" "}
                  {meta.min_margin_pct}% minimum: {lowMarginLines.map((l) => l.item).filter(Boolean).join(", ")}
                </div>
                <button
                  type="button"
                  data-testid="review-low-margin-fix"
                  onClick={() => setStep(3)}
                  className="mt-2 rounded-full border border-red-400/50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 hover:bg-red-500/20"
                >
                  Back to lines
                </button>
              </div>
            )}
            {meta.show_profit_strip && totals?.cost_lines > 0 && (
              <div
                className="no-print mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-500/25 bg-emerald-500/5 px-4 py-3"
                data-testid="review-profit-strip"
              >
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300/80">
                  Shop profit (staff only)
                </span>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono-data text-sm">
                  <span className="text-zinc-400">
                    Cost <span className="text-zinc-200">{money(totals.cost_total)}</span>
                  </span>
                  <span className="text-zinc-400">
                    Profit <span className="font-bold text-emerald-300">{money(totals.profit)}</span>
                  </span>
                  <span className="text-zinc-400">
                    Margin <span className="font-bold text-emerald-300">{totals.margin_pct}%</span>
                  </span>
                </div>
              </div>
            )}
            {!isNew && <RoomMockups quoteId={id} stone={q.slab_color} />}
            <ApprovalTrail quote={q} compact />
            <div className="no-print mb-4 flex flex-wrap items-center gap-2" data-testid="quote-review-actions">
              <Button
                data-testid="email-quote-button"
                variant="outline"
                disabled={isNew}
                onClick={sendEmail}
                className="h-11 border-zinc-800 bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                <PaperPlaneTilt size={15} weight="bold" className="mr-2" />{" "}
                Email quote
              </Button>
              <Button
                data-testid="accept-quote-button"
                variant="outline"
                disabled={isNew || !!q.links?.job_id}
                onClick={accept}
                title={
                  q.links?.job_id
                    ? "This quote already started its project"
                    : undefined
                }
                className="h-11 bg-emerald-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400 disabled:opacity-100"
              >
                <Briefcase size={15} weight="bold" className="mr-2" />
                Accept → start project
              </Button>
              {(q.links?.project_id || q.links?.job_id) && (
                <Button
                  data-testid="open-project-button"
                  onClick={() =>
                    navigate(
                      q.links.project_id
                        ? `/projects/${q.links.project_id}`
                        : `/projects/${q.links.job_id}?tab=field`,
                    )
                  }
                  className="h-11 border border-emerald-500/60 bg-emerald-500/20 text-xs font-semibold uppercase tracking-widest text-emerald-200 hover:bg-emerald-500/30"
                >
                  <ArrowSquareOut size={15} weight="bold" className="mr-2" />
                  {q.links.project_id ? "Open project" : "Open job"}
                  {(q.links.project_code || q.links.job_code) && (
                    <span className="action-code ml-2 font-mono-data text-[11px] text-emerald-300/70">
                      {q.links.project_code || q.links.job_code}
                    </span>
                  )}
                </Button>
              )}
              {(q.links?.project_id || q.links?.job_id) && !isNew && (
                <div
                  data-testid="quote-linked-project-note"
                  className="basis-full text-[11px] text-zinc-400"
                >
                  Already tied to{" "}
                  <Link
                    data-testid="quote-linked-project-link"
                    to={
                      q.links.project_id
                        ? `/projects/${q.links.project_id}`
                        : `/projects/${q.links.job_id}?tab=field`
                    }
                    className="font-mono-data font-semibold text-emerald-300 underline-offset-2 hover:underline"
                  >
                    {q.links.project_code || q.links.job_code || "a project"}
                  </Link>
                  {q.links.job_id
                    ? " — project already started; accept and create are locked. Use More → Unlink to move it."
                    : " — accepting will start work on this project. Use More → Unlink to move it elsewhere."}
                </div>
              )}
              {(q.links?.job_missing || q.links?.project_missing) && (
                <div
                  data-testid="quote-stale-link-note"
                  className="basis-full rounded-md border border-zinc-700 bg-zinc-800/40 px-3 py-2 text-[11px] text-zinc-400"
                >
                  {q.links.job_missing && q.links.project_missing
                    ? "The job and project that were linked to this quote have been removed. You can create new ones below."
                    : q.links.job_missing
                      ? "The job that was linked to this quote has been removed — create a new one below when you're ready."
                      : "The project that was linked to this quote has been removed — create a new one below when you're ready."}
                </div>
              )}
              {!q.links?.project_id && (
                <Button
                  data-testid="create-project-button"
                  variant="outline"
                  disabled={isNew}
                  onClick={createProject}
                  className="h-11 border-indigo-500/40 bg-indigo-500/10 text-xs uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20"
                >
                  <Stack size={15} weight="bold" className="mr-2" /> Create project
                </Button>
              )}
              {!isNew && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="quote-review-more"
                      variant="outline"
                      className="ml-auto h-11 border-zinc-700 px-3 text-xs uppercase tracking-widest text-zinc-300 hover:text-white"
                    >
                      <DotsThreeVertical size={18} weight="bold" className="mr-1" />
                      More
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem
                      data-testid="toggle-drawings-button"
                      onClick={() =>
                        setPortalFlag({ show_drawings: q.show_drawings === false })
                      }
                    >
                      {q.show_drawings !== false ? (
                        <Eye size={15} weight="bold" className="mr-2" />
                      ) : (
                        <EyeSlash size={15} weight="bold" className="mr-2" />
                      )}
                      {q.show_drawings !== false ? "Drawings shown" : "Drawings hidden"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="approve-manual-button"
                      disabled={busy || q.status === "Accepted"}
                      onClick={approveManually}
                    >
                      <SealCheck size={15} weight="bold" className="mr-2" />
                      {waived ? "Accept — no signature" : "Accept manually"}
                    </DropdownMenuItem>
                    {q.links?.project_id && (
                      <DropdownMenuItem
                        data-testid="unlink-project-button"
                        disabled={busy}
                        onClick={unlinkProject}
                      >
                        <LinkBreak size={15} weight="bold" className="mr-2" />
                        Unlink from {q.links.project_code || "project"}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {commission?.eligible && (
              <div
                data-testid="quote-commission-preview"
                className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-100"
              >
                <HandCoins size={14} weight="bold" className="text-emerald-300" />
                <span className="font-semibold">{commission.org_name}</span>
                <span className="text-emerald-200/80">
                  earns {commission.mode === "per_sf" ? `$${commission.rate}/sf × ${commission.sqft} sf` : `${commission.rate}%`}
                </span>
                <span className="ml-auto font-mono-data text-sm font-bold text-emerald-300" data-testid="quote-commission-amount">
                  ${Number(commission.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
                <span className="rounded-full border border-emerald-500/40 px-1.5 py-0.5 text-[9px] uppercase tracking-wider">
                  {commission.credited ? "credited" : commission.trigger === "paid" ? "on invoice paid" : "on acceptance"}
                </span>
              </div>
            )}
            {(q.stock_holds || []).length > 0 && (
              <div
                data-testid="quote-held-stock"
                className="mt-3 rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3"
              >
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-indigo-200">
                  <Package size={13} weight="bold" /> Held from stock ({q.stock_holds.length})
                  {stockAreas.map((a) => (
                    <span
                      key={a.area_id}
                      data-testid={`quote-onhand-${a.area_id}`}
                      title="Real on-hand count from inventory right now"
                      className={`rounded-full border px-1.5 py-0.5 text-[9px] tracking-wider ${
                        a.options.pieces.length + a.options.remnants.length > 0
                          ? "border-emerald-500/40 text-emerald-300"
                          : "border-red-500/40 text-red-300"
                      }`}
                    >
                      {a.name || "Area"}: {a.options.pieces.length} on hand
                      {a.options.remnants.length ? ` · ${a.options.remnants.length} rem` : ""}
                    </span>
                  ))}
                  <button
                    type="button"
                    data-testid="quote-held-manage"
                    onClick={() => setStockOpen(true)}
                    className="ml-auto rounded-full border border-indigo-500/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
                  >
                    Manage
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {q.stock_holds.map((h) => (
                    <span
                      key={h.id}
                      data-testid={`quote-held-${h.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-semibold text-indigo-100"
                    >
                      <span className="rounded bg-indigo-500/25 px-1 text-[8px] font-bold uppercase tracking-widest text-indigo-200">
                        {KIND_LABEL[h.kind] || h.kind}
                      </span>
                      {h.label || "held"}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!isNew && approval.status === "approved" && (
              <div
                data-testid="drawing-approval-card"
                className="no-print mb-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3.5"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-500/20 text-emerald-300">
                    <SealCheck size={15} weight="bold" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Drawing sign-off
                    </div>
                    <p className="mt-1 text-sm text-zinc-200" data-testid="drawing-approval-status">
                      {`Approved by ${approval.name} on ${new Date(approval.at).toLocaleString()}`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0">
                <div className="no-print mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  <Eye size={13} weight="bold" className="text-indigo-300" />
                  Customer preview
                  <span className="ml-auto rounded-full border border-zinc-800 px-2 py-0.5 text-[9px] tracking-wider text-zinc-500">
                    Exactly what they receive
                  </span>
                </div>
                <div
                  id="print-area"
                  className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/40 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.9)]"
                >
                  <QuotePrintout
                  quote={q}
                  totals={totals}
                  template={template}
                  edgeStyles={meta.edge_styles}
                  logoUrl={hasLogo ? `${API}/public/logo` : ""}
                  coverUrl={
                    id && id !== "new"
                      ? `${API}/public/quote-cover/${id}?w=520`
                      : ""
                  }
                  heroUrl={
                    id && id !== "new" && q.design?.hero
                      ? `${API}/public/quote-hero/${id}?w=1400&v=${encodeURIComponent(q.design.hero.created_at || "")}`
                      : ""
                  }
                />
                </div>
              </div>

              <aside
                className="no-print space-y-4 xl:sticky xl:top-6"
                data-testid="quote-notes-panel"
              >
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Notes shown to the customer
                  </Label>
                  <Textarea
                    data-testid="quote-notes-input"
                    value={q.notes}
                    onChange={(e) => setQ({ ...q, notes: e.target.value })}
                    placeholder="Add any notes the customer should see on the quote…"
                    className="mt-2 min-h-[120px] border-zinc-800 bg-zinc-950 text-sm"
                  />
                </div>
                {!isNew && q.contract_signature && (
                  <div
                    className="rounded-lg border border-emerald-700/40 bg-emerald-500/5 p-4"
                    data-testid="quote-contract-signed"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">
                      Agreement signed
                    </div>
                    <p className="mt-1 text-sm text-zinc-200">
                      {q.contract_signature.title || "Agreement"} signed by{" "}
                      <strong>{q.contract_signature.name}</strong>
                    </p>
                    <p className="text-xs text-zinc-500">
                      {new Date(q.contract_signature.at).toLocaleString()}
                    </p>
                    {q.contract_signature.image && (
                      <img
                        src={q.contract_signature.image}
                        alt="Signature"
                        className="mt-2 h-14 rounded bg-white p-1"
                      />
                    )}
                    <button
                      type="button"
                      data-testid="quote-contract-print"
                      onClick={() => printSignedAgreement(q.contract_signature)}
                      className="mt-2 text-[11px] font-bold uppercase tracking-widest text-emerald-300 transition-colors hover:text-emerald-200"
                    >
                      Print agreement
                    </button>
                  </div>
                )}
                {!isNew && <PortalThread kind="quote" recordId={id} />}
              </aside>
            </div>
          </div>
        )}
      </main>

      <MoveAccountDialog
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        onDone={() => window.location.reload()}
        endpoint={`/quotes/${id}/move-account`}
        title="Move quote to another account"
        description="Moves this quote and its invoices to another account."
        confirmLabel="Move quote"
        excludeId={q.customer_id}
      />

      <Dialog open={!!newAcc} onOpenChange={(o) => !o && setNewAcc(null)}>
        <DialogContent
          className="border-zinc-800 bg-zinc-900"
          data-testid="new-account-dialog"
        >
          <DialogHeader>
            <DialogTitle>New account</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["name", "Name", "Maria Lopez"],
              ["company", "Company", "Lopez Builders"],
              ["phone", "Phone", "(555) 201-8890"],
              ["email", "Email", "maria@builders.com"],
              ["address", "Billing address", "142 Elm St, Springfield"],
            ].map(([key, label, ph]) => (
              <div key={key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {label}
                </Label>
                <Input
                  data-testid={`new-account-${key}`}
                  value={newAcc?.[key] || ""}
                  onChange={(e) =>
                    setNewAcc((f) => ({ ...f, [key]: e.target.value }))
                  }
                  placeholder={ph}
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Group
              </Label>
              <Select
                value={newAcc?.group || "Regular"}
                onValueChange={(v) => setNewAcc((f) => ({ ...f, group: v }))}
              >
                <SelectTrigger
                  data-testid="new-account-group"
                  className="h-11 border-zinc-800 bg-zinc-950"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CUSTOMER_GROUPS.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="new-account-cancel"
              variant="outline"
              onClick={() => setNewAcc(null)}
              className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              data-testid="new-account-save"
              onClick={saveNewAccount}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              Add account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!newContact}
        onOpenChange={(o) => !o && setNewContact(null)}
      >
        <DialogContent
          className="border-zinc-800 bg-zinc-900"
          data-testid="new-contact-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              New contact{account ? ` for ${account.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["name", "Name", "Sam Super"],
              ["role", "Role", "Site superintendent"],
              ["phone", "Phone", "(555) 201-8890"],
              ["email", "Email", "sam@builders.com"],
            ].map(([key, label, ph]) => (
              <div key={key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {label}
                </Label>
                <Input
                  data-testid={`new-contact-${key}`}
                  value={newContact?.[key] || ""}
                  onChange={(e) =>
                    setNewContact((f) => ({ ...f, [key]: e.target.value }))
                  }
                  placeholder={ph}
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              data-testid="new-contact-cancel"
              variant="outline"
              onClick={() => setNewContact(null)}
              className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              data-testid="new-contact-save"
              onClick={saveNewContact}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              Add contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newSlab} onOpenChange={(o) => !o && setNewSlab(null)}>
        <DialogContent
          className="border-zinc-800 bg-zinc-900"
          data-testid="new-slab-dialog"
        >
          <DialogHeader>
            <DialogTitle>New slab color</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["slab_color", "Color", "Taj Mahal Quartzite"],
              ["material", "Material", "Quartzite"],
              ["finish", "Finish", "Polished"],
              ["thickness", "Thickness", "3cm"],
              ["supplier", "Supplier", "StoneCo"],
              ["stone_group", "Stone group", "Group 0"],
              ["length_in", "Length (in)", "126"],
              ["width_in", "Width (in)", "63"],
              ["quantity", "Slabs on hand", "1"],
            ].map(([key, label, ph]) => (
              <div key={key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {label}
                </Label>
                <Input
                  data-testid={`new-slab-${key}`}
                  value={newSlab?.[key] ?? ""}
                  onChange={(e) =>
                    setNewSlab((f) => ({ ...f, [key]: e.target.value }))
                  }
                  placeholder={ph}
                  className="h-11 border-zinc-800 bg-zinc-950"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              data-testid="new-slab-cancel"
              variant="outline"
              onClick={() => setNewSlab(null)}
              className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              data-testid="new-slab-save"
              onClick={saveNewSlab}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              Add slab
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
